import * as Api from '@ViewModels';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';

const BASE_QUERY_KEY = 'survey/fileRequest/response/filter';
const SURVEY_QUERY_KEY = 'survey/fileRequest';
const PAGE_SIZE = 100;

export function useContactFileRequestsQuery({ contactId, enabled = true }: { contactId: string; enabled?: boolean }) {
	const userSession = useUserSession();

	return useQuery<Api.IFileRequestSurveyResponse[]>({
		enabled: enabled && !!contactId,
		queryFn: async () => {
			const filter: Api.IFileRequestSurveyResponseFilterRequest = {
				_type: 'FileRequestSurveyResponseFilterRequest',
				contactId,
			};
			const responses: Api.IFileRequestSurveyResponse[] = [];
			let pageToken: string | undefined;
			do {
				const page = await userSession.webServiceHelper.callAsync<Api.IPagedCollection<Api.IFileRequestSurveyResponse>>(
					Api.ImpersonationBroker.composeApiUrl({
						queryParams: { pageSize: PAGE_SIZE, pageToken },
						urlPath: 'survey/fileRequest/response/filter',
					}),
					'POST',
					filter
				);
				responses.push(...(page?.values ?? []));
				pageToken = page?.pageToken || undefined;
			} while (pageToken);
			return responses;
		},
		queryKey: [BASE_QUERY_KEY, contactId],
		meta: {
			toastErrorMessage: 'Failed to load file requests',
			errorLogging: { action: 'ContactFiles/loadFileRequests' },
		},
	});
}

export function invalidateContactFileRequestsQueries({ contactId }: { contactId: string }) {
	return reactQueryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY, contactId] });
}

export function useFileRequestSurveyQueries({ ids }: { ids: string[] }) {
	const userSession = useUserSession();

	return useQueries({
		queries: ids.map(id => ({
			queryFn: () =>
				userSession.webServiceHelper.callAsync<Api.IFileRequestSurvey>(
					Api.ImpersonationBroker.composeApiUrl({ urlPath: `survey/fileRequest/${id}` }),
					'GET'
				),
			queryKey: [SURVEY_QUERY_KEY, id],
		})),
	});
}
